import { Product, ProductSlug } from "@/api/product-api";

/**
 * 단품 → **수량 세트** 제품 매핑 (예: MOLLE Pouch 1개 ↔ MOLLE Pouch (Set) = 같은 파우치 2개).
 *
 * 세트는 데이터상 별도 `BUNDLE` 제품이지만 사용자에게는 **단품의 수량**으로 보인다.
 * 그래서 글러브 사이즈 세트(`tactglove-dk3-bundle`)처럼 **목록에는 세우지 않고**
 * (`ProductUtil.isUnlistedBundle`), 단품을 고르는 자리에서만 세트가로 바꾼다:
 *
 * - 단품 PDP: 「1개 / 세트」 선택지 (`[slug].tsx`)
 * - 장비 PDP 의 액세서리 행 / 카트: 단품 N개 → 세트 ⌊N/units⌋ + 단품 나머지
 *   (`libs/cart/pack-set-lines.ts`)
 *
 * ⚠️ **번들 머저에 맡기지 않는다.** 머저(`bundle-merger.ts`)는 구성품마다 「수량 > 0」 만
 * 봐서 **같은 SKU 가 두 번 든 구성품**을 못 센다 — 단품 1개만 있어도 세트를 만든다.
 * 그 경로는 `isUnlistedBundle` 로 막고, 묶음은 위 함수 하나가 한다.
 *
 * ⚠️ **slug 패턴(`-set`)으로 추론하지 않는다** — `bundle-base.ts` 가 `-bundle` 접미사를
 * 금지한 것과 같은 이유다(`docs/product-variant-line-policy.md` §2). 새 세트는 여기 한 줄.
 */
export const SET_VARIANT_BY_SLUG: Partial<Record<string, ProductSlug>> = {
  [ProductSlug.MOLLE_POUCH_FOR_TACTSUIT_AIR_CAMO]:
    ProductSlug.MOLLE_POUCH_FOR_TACTSUIT_AIR_CAMO_SET,
};

/** 이 slug 가 수량 세트 제품인가 (= 목록에서 숨길 대상). */
export const isSetVariantSlug = (slug: string): boolean =>
  Object.values(SET_VARIANT_BY_SLUG).includes(slug as ProductSlug);

/** 세트 슬러그 → 그 단품 슬러그. 세트가 아니면 `null`. */
export const setBaseOf = (slug: string): string | null =>
  Object.entries(SET_VARIANT_BY_SLUG).find(([, set]) => set === slug)?.[0] ??
  null;

export type SetDeal = {
  base: Product;
  set: Product;
  baseSku: string;
  setSku: string;
  /** 세트 1개 = 단품 몇 개인가. 카탈로그의 components 에서 **센다**(하드코딩 아님). */
  units: number;
};

/**
 * 단품 slug 의 세트 짝을 **카탈로그로 검증해서** 돌려준다. 하나라도 어긋나면 `null`.
 *
 * 맵은 짝만 선언하고, 「세트 = 단품 N개」는 데이터가 말하게 한다. 그래서 다음 중 하나라도
 * 성립하지 않으면 세트로 묶지 않는다 — 묶지 않으면 **단품 정가로 청구**될 뿐이지만,
 * 잘못 묶으면 **다른 물건이 결제**된다(실패 방향을 그쪽으로 고정한다):
 *
 * - 둘 다 카탈로그에 있다(세트가 판매중지면 공개 API 에서 레코드째 빠진다)
 * - 둘 다 SKU 옵션이 정확히 하나다
 * - 세트의 components 가 **전부 단품 SKU** 이고 2개 이상이다
 */
export const getSetDeal = (
  products: Product[],
  baseSlug: string,
): SetDeal | null => {
  const setSlug = SET_VARIANT_BY_SLUG[baseSlug];
  if (!setSlug) return null;

  const base = products.find((p) => p.slug === baseSlug);
  const set = products.find((p) => p.slug === setSlug);
  if (!base || !set) return null;
  if (base.skuOptions.length !== 1 || set.skuOptions.length !== 1) return null;

  const baseSku = base.skuOptions[0].sku;
  const components = set.skuOptions[0].components;
  if (components.length < 2 || !components.every((c) => c === baseSku)) {
    return null;
  }

  return {
    base,
    set,
    baseSku,
    setSku: set.skuOptions[0].sku,
    units: components.length,
  };
};

/**
 * 단품 PDP 의 **수량 세트 선택을 주소에 싣는 쿼리**(#6008 R1) — `?pack=set` · `?pack=single`.
 * 번들의 `?bundle=` 과 같은 역할이다. 화면(첫 선택) · SSR 구조화 데이터(`resolveSeoOffer`) ·
 * GMC 피드 링크가 같은 값을 읽어 「피드 항목 → 그 항목이 골라진 화면 · 그 가격의 구조화 데이터」가 된다.
 * 없으면 기본(거래가 성립하면 세트)이다.
 */
export const SET_PACK_QUERY = "pack";
export type SetPack = "set" | "single";
export const parseSetPack = (raw: unknown): SetPack | null =>
  raw === "set" || raw === "single" ? raw : null;
